import {
  useApiClient,
  WebshopShoppingCartCommands,
  WebshopShoppingCartEvent,
  WebshopShoppingCartQueries,
  WebshopShoppingCartVersioned,
  webshopShoppingCartAddProductService,
} from '@cldsystems/ws-api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from '@/providers/app-provider/app-provider';
import { MyCartActionButtonsEnum } from '@/types/action-buttons/action-buttons';
import { UpdateMutateProps } from '@/types/common';
import { SimpleProductInfo } from '@/types/products/simple-product-info';
import { WithQuantity } from '@/types/products/with-quantity';
import {
  getApiErrorMessage,
  isNoChangesError,
  isOptimisticLockError,
} from '@/utils/get-api-error-message';
import { priceCheck } from '@/utils/price-check';

type ShoppingCartAddProductMutationPayload = {
  productId: string;
  quantity: number;
  event: WebshopShoppingCartEvent;
  product: WithQuantity<SimpleProductInfo>;
  operationId: string;
  buttonType?: MyCartActionButtonsEnum;
  optimisticApplied?: boolean;
  productWasInMyCart: boolean;
  previousQuantity: number;
  /** Skip the error toast for a (retryable) optimistic-lock conflict. */
  suppressVersionConflictToast?: boolean;
};

type ShoppingCartAddProductMutationContext = {
  shoppingCartId: string;
  productId: string;
  operationId: string;
  productWasInMyCart: boolean;
  previousQuantity: number;
  previousVersion?: number;
};

const useShoppingCartAddProductMutate = (
  markMyCartPending: (
    productId: string,
    buttonType: MyCartActionButtonsEnum | undefined,
    operationId: string,
  ) => void,
  clearMyCartPending: (productId: string, operationId?: string) => void,
  getMyCartPending: (productId: string) =>
    | {
        operationId: string;
      }
    | undefined,
  setIsPendingUpdateMyCartProductId: (id: string | undefined) => void,
  setActionButtonType: (buttonType: MyCartActionButtonsEnum | undefined) => void,
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { httpClient, hasSchema } = useApiClient();
  return useMutation({
    mutationKey: [WebshopShoppingCartCommands.AddProduct],
    mutationFn: ({ params, payload }: UpdateMutateProps<ShoppingCartAddProductMutationPayload>) =>
      webshopShoppingCartAddProductService({
        httpClient,
        hasSchema: Boolean(hasSchema),
      })({
        params,
        payload: {
          products: [{ id: payload!.productId, quantity: payload!.quantity }],
          event: payload!.event,
        },
      }),
    onMutate: async ({ params, payload }) => {
      if (!payload?.product || payload.productId !== payload.product.id) return;

      const { product } = payload;
      markMyCartPending(payload.productId, payload.buttonType, payload.operationId);
      setIsPendingUpdateMyCartProductId(payload.productId);
      setActionButtonType(payload.buttonType);
      const addedQuantity = payload.quantity ?? product.quantity;

      const queryKey = [WebshopShoppingCartQueries.GetById, params.id];
      await queryClient.cancelQueries({ queryKey });
      const previousShoppingCart = queryClient.getQueryData<WebshopShoppingCartVersioned>(queryKey);
      queryClient.setQueryData<WebshopShoppingCartVersioned>(queryKey, old => {
        if (!old) return undefined;

        const isProductExists = !!old.shoppingCart.products.find(p => p.id === payload.productId);

        const products = payload.optimisticApplied
          ? old.shoppingCart.products
          : isProductExists
            ? old.shoppingCart.products.map(p =>
                p.id === product.id ? { ...p, quantity: p.quantity + addedQuantity } : p,
              )
            : [
                ...old.shoppingCart.products,
                {
                  ...product,
                  quantity: addedQuantity,
                  discountType: product.discountType,
                  display: product.display,
                  minOrderQuantity: 1,
                },
              ];
        const newAmount = products.reduce(
          (acc, current) => acc + current.price * current.quantity,
          0,
        );
        const newDiscountedAmount = products.reduce(
          (acc, current) => acc + current.discountedPrice * current.quantity,
          0,
        );

        return {
          ...old,
          version: old.version + 1,
          shoppingCart: {
            ...old.shoppingCart,
            amount: priceCheck(newAmount),
            discountedAmount: priceCheck(newDiscountedAmount),
            totalAmount: priceCheck(newDiscountedAmount),
            products,
          },
        };
      });

      return {
        shoppingCartId: params.id,
        productId: payload.productId,
        operationId: payload.operationId,
        productWasInMyCart: payload.productWasInMyCart,
        previousQuantity: payload.previousQuantity,
        previousVersion: previousShoppingCart?.version,
      } satisfies ShoppingCartAddProductMutationContext;
    },
    onError: (err, variables, context) => {
      if (context) {
        const currentPending = getMyCartPending(context.productId);

        if (!currentPending || currentPending.operationId === context.operationId) {
          const queryKey = [WebshopShoppingCartQueries.GetById, context.shoppingCartId];
          queryClient.setQueryData<WebshopShoppingCartVersioned>(queryKey, old => {
            if (!old) return old;

            const products = context.productWasInMyCart
              ? old.shoppingCart.products.map(product =>
                  product.id === context.productId
                    ? { ...product, quantity: context.previousQuantity }
                    : product,
                )
              : old.shoppingCart.products.filter(product => product.id !== context.productId);
            const newAmount = products.reduce(
              (acc, current) => acc + current.price * current.quantity,
              0,
            );
            const newDiscountedAmount = products.reduce(
              (acc, current) => acc + current.discountedPrice * current.quantity,
              0,
            );

            return {
              ...old,
              version: context.previousVersion ?? old.version,
              shoppingCart: {
                ...old.shoppingCart,
                amount: priceCheck(newAmount),
                discountedAmount: priceCheck(newDiscountedAmount),
                totalAmount: priceCheck(newDiscountedAmount),
                products,
              },
            };
          });
        }

        // No-op commands ("no changes detected") are harmless. Optimistic-lock
        // conflicts are retried with the real version by the caller; stay silent
        // on the first failure so we only notify if the retry also fails.
        const isSuppressedVersionConflict =
          variables.payload?.suppressVersionConflictToast && isOptimisticLockError(err);
        if (!isNoChangesError(err) && !isSuppressedVersionConflict) {
          toast.error(getApiErrorMessage(err, t?.common.productAddToMyCartFailed));
        }
      }
      if (context) {
        clearMyCartPending(context.productId, context.operationId);
      }
      setIsPendingUpdateMyCartProductId(undefined);
      setActionButtonType(undefined);
    },
    onSettled: (_data, _error, _variables, context) => {
      if (context) {
        clearMyCartPending(context.productId, context.operationId);
      }
      setIsPendingUpdateMyCartProductId(undefined);
      setActionButtonType(undefined);
    },
  });
};

export default useShoppingCartAddProductMutate;
